import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Divider,
  Grid,
  Loader,
  Feed,
  Dimmer,
  DropdownProps,
  MenuItemProps,
  BreadcrumbSectionProps,
  ButtonProps,
} from 'semantic-ui-react';
import _ from 'lodash';
import moment from 'moment';
import { startOfDay, addDays } from 'date-fns';
import { apis } from '../../api';
import QuestionItem from './QuestionItem';
import ValidationRequestModal from '../ValidationRequestModal';
import BatchAnswerModal from './BatchAnswerModal';
import QuestionSideBar from './QuestionSideBar';
import AddTemplateModal from './AddTemplateModal';
import OrderTemplateModal from './OrderTemplateModal';
import EditTemplateModal from './EditTemplateModal';
import FeedControl from './FeedControl';
import {
  Question,
  QuestionCategory,
  QuestionChallenge,
  QuestionTemplate,
  QuestionChallengeOption,
  QuestionMainCategory,
  QuestionSubCategory,
} from '@types';

export const DIVIDE_SIZE = 3;

export const NO_TAG_FILTER_VALUE = '__NO_TAG__';

export enum QuestionSortType {
  LATEST = 'latest',
  OLDEST = 'oldest',
  END_DATE = 'endDate',
  RESULT_DATE = 'resultDate',
  CHALLENGE_QUESTION_COUNT = 'challengeQuestionCount',
}

type QuestionContainerProps = {
  questionList: Question[];
  questionTagList: string[];
  questionTemplateList: QuestionTemplate[];
  filterStartDatetime: string;
  filterEndDatetime: string;
  setFilterStartDatetime: React.Dispatch<React.SetStateAction<string>>;
  setFilterEndDatetime: React.Dispatch<React.SetStateAction<string>>;
  onAddQuestionTemplate: (values: any) => Promise<void>;
  onEditQuestionTemplate: (values: any) => void;
  onUpdateQuestionTemplateOrder: (values: any) => void;
  onDeleteQuestionTemplate: (values: any) => void;
  onAnswerQuestionList: any;
  onRefreshQuestionPress: () => void;
  isLoading: boolean;
};

const QuestionContainer = ({
  questionList,
  questionTagList,
  questionTemplateList,
  filterStartDatetime,
  filterEndDatetime,
  setFilterStartDatetime,
  setFilterEndDatetime,
  onAddQuestionTemplate,
  onEditQuestionTemplate,
  onUpdateQuestionTemplateOrder,
  onDeleteQuestionTemplate,
  onAnswerQuestionList,
  onRefreshQuestionPress,
  isLoading,
}: QuestionContainerProps) => {
  const [batchAnswerModalOpen, setBatchAnswerModalOpen] =
    useState<boolean>(false);
  const [selectedChallengeIds, setSelectedChallengeIds] = useState<number[]>(
    [],
  );

  const [selectedChallengeType, setSelectedChallengeType] =
    useState<string>('PURCHASE_CHALLENGE');
  const [questions, setQuestions] = useState<Question[]>([]);

  const [questionCount, setQuestionCount] = useState<{
    purchaseChallengeQuestion: number;
    collaboQuestion: number;
    raceQuestion: number;
  }>({
    purchaseChallengeQuestion: 0,
    collaboQuestion: 0,
    raceQuestion: 0,
  });

  const [questionAllTagOptions, setQuestionAllTagOptions] = useState<
    {
      key: string;
      text: string;
      value: string;
    }[]
  >([]);
  const [questionAllTagOptionsWithNumber, setQuestionAllTagOptionsWithNumber] =
    useState<
      {
        text: string;
        key: string;
        value: string;
      }[]
    >([]);

  const [mainCategories, setMainCategories] = useState<QuestionMainCategory[]>(
    [],
  );
  const [subCategories, setSubCategories] = useState<QuestionSubCategory[]>([]);
  const [challengeOptions, setChallengeOptions] = useState<
    QuestionChallengeOption[]
  >([]);
  const [questionCategories, setQuestionCategories] = useState<
    QuestionCategory[]
  >([]);

  const [activeMainCategory, setActiveMainCategory] = useState<string>('ALL');

  const [isAnswerFilter, setIsAnswerFilter] = useState<
    'notAnswered' | 'answered'
  >('notAnswered');
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [divideGroup, setDivideGroup] = useState<number>(-1); // 전체
  const [addTemplateModalOpen, setAddTemplateModalOpen] =
    useState<boolean>(false);
  const [editTemplateModalOpen, setEditTemplateModalOpen] =
    useState<boolean>(false);
  const [orderTemplateModalOpen, setOrderTemplateModalOpen] =
    useState<boolean>(false);

  const [validationModalOpen, setValidationModalOpen] =
    useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(true); // NOTE: 다운로드 요청 사유를 받아야 하는 경우 default false
  const [questionsForDownload, setQuestionsForDownload] = useState<any>([]);
  const [downloading, setDownloading] = useState<boolean>(false);

  const [sortType, setSortType] = useState<QuestionSortType>(
    QuestionSortType.LATEST,
  );

  const [selectedTagsForFilter, setSelectedTagsForFilter] = useState<string[]>(
    [],
  );
  const [selectedCategory, setSelectedCategory] = useState<{
    mainCategory: string;
    subCategoryIds: number[];
  }>({
    mainCategory: '',
    subCategoryIds: [],
  });

  useEffect(() => {
    setFilter();
  }, [
    sortType,
    questions,
    isAnswerFilter,
    selectedCategory.mainCategory,
    selectedCategory.subCategoryIds,
    divideGroup,
    questionAllTagOptions,
    selectedChallengeType,
    selectedChallengeIds,
    selectedTagsForFilter,
  ]);

  useEffect(() => {
    getQuestionCategories();
  }, []);

  useEffect(() => {
    setSelectedChallengeIds([]);
  }, [selectedChallengeType, isAnswerFilter]);

  useEffect(() => {
    if (!filteredQuestions) return;
    initializeCategories();
    classifyChallengeOption();
  }, [filteredQuestions]);

  useEffect(() => {
    classifyQuestionList();
  }, [questionList]);

  useEffect(() => {
    getQuestionTags();
  }, [questionTagList]);

  const getQuestionCategories = async () => {
    const { questionCategories: _questionCategories } =
      await apis.question.getQuestionCategories();
    setQuestionCategories(_questionCategories);
  };

  const classifyQuestionList = async () => {
    const _questions = [] as Question[];

    let _purchaseChallengeQuestionCount = 0;
    let _collaboQuestionCount = 0;
    let _raceQuestionCount = 0;
    questionList
      .filter(
        (_question: Question) => _question.title.indexOf('[챌린지 개설]') < 0,
      )
      .forEach((_question: Question) => {
        _questions.push(_question);
        if (_question.challenge) {
          if (_question.challenge.challengeType === 'collabo') {
            _collaboQuestionCount += 1;
          }
        }
        if (_question.serviceType === 'PURCHASE_CHALLENGE') {
          _purchaseChallengeQuestionCount += 1;
        }
        if (_question.serviceType === 'RACE') {
          _raceQuestionCount += 1;
        }
      });
    setQuestions(_questions);

    setQuestionCount({
      collaboQuestion: _collaboQuestionCount,
      purchaseChallengeQuestion: _purchaseChallengeQuestionCount,
      raceQuestion: _raceQuestionCount,
    });
  };

  const classifyChallengeOption = async () => {
    const removeDuplicates = (arr: QuestionChallengeOption[]) => {
      const map = new Map();

      return arr.filter((item) => {
        const key = `${item.key}-${item.challengeType}`;

        if (map.has(key)) {
          return false;
        }

        map.set(key, true);

        return true;
      });
    };

    // 챌린지 옵션은 dropdown 필터에 영향받지 않고
    // 현재 챌린지 타입 내의 모든 챌린지를 보여주기 위해 questions 전체를 사용
    const _baseQuestions = questions.filter((question) =>
      filterByselectedChallengeType(question),
    );

    const _challengeOptions = _baseQuestions
      .filter((_question: Question) => {
        return (
          _question.title.indexOf('[챌린지 개설]') < 0 &&
          ((_question.serviceType === 'CHALLENGE' && _question.challenge) ||
            (_question.serviceType === 'PURCHASE_CHALLENGE' &&
              _question.purchaseChallenge))
        );
      })
      .map((_question: Question) => {
        const challenge =
          _question.serviceType === 'CHALLENGE'
            ? _question.challenge
            : {
                ..._question.purchaseChallenge,
                challengeType: 'PURCHASE',
              };

        return {
          key: challenge!.id!,
          text: `[${challenge!.id}] ${challenge!.title}`,
          value: challenge!.id!,
          challengeType: String(challenge!.challengeType),
        };
      });

    setChallengeOptions(removeDuplicates(_challengeOptions));
  };

  const filterByselectedChallengeType = (question: Question) => {
    if (['RACE', 'PURCHASE_CHALLENGE'].includes(selectedChallengeType)) {
      return question.serviceType === selectedChallengeType;
    }
    if (selectedChallengeType === 'CHALLENGE') {
      return (
        question.serviceType === selectedChallengeType &&
        question.challenge?.challengeType !== 'collabo'
      );
    }
    return question.challenge?.challengeType === 'collabo';
  };

  const initializeCategories = () => {
    const _mainCategories: QuestionMainCategory[] = [];
    const _subCategories: QuestionSubCategory[] = [];
    const _mainCategoryKeywords: string[] = [];

    const _questions = questions.filter((question: Question) =>
      filterByselectedChallengeType(question),
    );
    const _questionCategories = [
      'CHALLENGE',
      'RACE',
      'PURCHASE_CHALLENGE',
    ].includes(selectedChallengeType)
      ? questionCategories.filter(
          (_category: QuestionCategory) =>
            _category.serviceType === selectedChallengeType,
        )
      : questionCategories.filter(
          (_category: QuestionCategory) =>
            _category.serviceType === 'CHALLENGE',
        );

    _mainCategories.push({
      key: 'ALL',
      value: 'ALL',
      text: `전체 (${_questions.length})`,
    });
    _questionCategories.forEach((category: QuestionCategory) => {
      if (!_mainCategoryKeywords.includes(category.mainCategory)) {
        _mainCategoryKeywords.push(category.mainCategory);
        const count = _questions.filter(
          (question: Question) =>
            question.category?.mainCategory === category.mainCategory,
        ).length;
        if (count > 0) {
          _mainCategories.push({
            key: category.mainCategory,
            value: category.mainCategory,
            text: category.mainCategory + ` (${count})`,
          });
        }
      }
      _subCategories.push({
        key: category.id,
        value: category.id,
        text: category.mainCategory + ' / ' + category.subCategory,
        mainCategory: category.mainCategory,
      });
    });
    setMainCategories(_mainCategories);
    setSubCategories(_subCategories);
  };

  const getFormatTagsObject = (_questions: Question[]) => {
    const numberOfTags = getNumberOfTags(getMergedTagsSingleArray(_questions));
    return Object.entries(numberOfTags).map(
      ([key, value]: [string, number]) => ({
        text: `${key} (${value})`,
        key,
        value: key,
      }),
    );
  };

  const filterByAnswer = (question: Question) => {
    return isAnswerFilter === 'notAnswered'
      ? question.answer === null
      : question.answer !== null;
  };

  const filterByDivideGroup = (question: Question) => {
    if (divideGroup < 0) {
      return true;
    }
    if (question.challenge) {
      return question.challenge.id % DIVIDE_SIZE === divideGroup;
    }
    return question.id % DIVIDE_SIZE === divideGroup;
  };

  const filterByDropdown = () => {
    const { mainCategory: _mainCategory, subCategoryIds: _subCategoryIds } =
      selectedCategory;
    let _filteredQuestions = [...questions];
    // 태그 (멀티 셀렉트: 선택된 태그 중 하나라도 매칭되면 포함)
    if (selectedTagsForFilter.length > 0) {
      _filteredQuestions = _filteredQuestions.filter((question) =>
        selectedTagsForFilter.some((selectedTag) => {
          if (selectedTag === NO_TAG_FILTER_VALUE) {
            return !question.tags || question.tags.length === 0;
          }
          return question.tags?.some((tag: string) => tag === selectedTag);
        }),
      );
    }
    // 메인카테고리
    if (_mainCategory) {
      if (_mainCategory !== 'ALL') {
        _filteredQuestions = _filteredQuestions.filter(
          (q) => q.category?.mainCategory === _mainCategory,
        );
      }
    }

    // 서브카테고리 (멀티 셀렉트)
    if (_subCategoryIds.length > 0) {
      _filteredQuestions = _filteredQuestions.filter(
        (q) => q.category?.id != null && _subCategoryIds.includes(q.category.id),
      );
    }

    // 챌린지 (멀티 셀렉트)
    if (selectedChallengeIds.length > 0) {
      _filteredQuestions = _filteredQuestions.filter((q) => {
        const challenge =
          selectedChallengeType === 'CHALLENGE'
            ? q.challenge
            : q.purchaseChallenge;

        return (
          challenge?.id != null && selectedChallengeIds.includes(challenge.id)
        );
      });
    }

    return _filteredQuestions;
  };

  const sortQuestion = (_filteredQuestions: Question[]) => {
    switch (sortType) {
      case QuestionSortType.END_DATE:
        return _filteredQuestions.sort((a, b) => {
          if (selectedChallengeType === 'CHALLENGE') {
            if (!a.challenge && b.challenge) return 1;
            if (a.challenge && !b.challenge) return -1;

            if (a.challenge?.endDate && b.challenge?.endDate) {
              return (
                new Date(a.challenge?.endDate).getTime() -
                new Date(b.challenge?.endDate).getTime()
              );
            }

            return 0;
          } else if (selectedChallengeType === 'PURCHASE_CHALLENGE') {
            if (!a.purchaseChallenge && b.purchaseChallenge) return 1;
            if (a.purchaseChallenge && !b.purchaseChallenge) return -1;

            if (a.purchaseChallenge?.endDate && b.purchaseChallenge?.endDate) {
              return (
                new Date(a.purchaseChallenge?.endDate).getTime() -
                new Date(b.purchaseChallenge?.endDate).getTime()
              );
            }

            return 0;
          } else {
            return 0;
          }
        });
      case QuestionSortType.RESULT_DATE:
        return _filteredQuestions.sort((a, b) => {
          if (!a.challenge && b.challenge) return 1;
          if (a.challenge && !b.challenge) return -1;

          if (a.challenge?.resultDate && b.challenge?.resultDate) {
            return (
              new Date(a.challenge?.endDate).getTime() -
              new Date(b.challenge?.endDate).getTime()
            );
          }
          return 0;
        });
      case QuestionSortType.OLDEST:
        return _filteredQuestions.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      case QuestionSortType.CHALLENGE_QUESTION_COUNT: {
        const NO_CHALLENGE_KEY = '__NO_CHALLENGE__';
        const getChallengeKey = (q: Question): number | string => {
          const challenge =
            selectedChallengeType === 'PURCHASE_CHALLENGE'
              ? q.purchaseChallenge
              : q.challenge;
          return challenge?.id ?? NO_CHALLENGE_KEY;
        };

        const challengeCountMap = new Map<number | string, number>();
        _filteredQuestions.forEach((q) => {
          const key = getChallengeKey(q);
          challengeCountMap.set(key, (challengeCountMap.get(key) ?? 0) + 1);
        });

        return _filteredQuestions.sort((a, b) => {
          const aKey = getChallengeKey(a);
          const bKey = getChallengeKey(b);

          // 챌린지 정보 없는 문의는 개수와 무관하게 항상 맨 뒤
          if (aKey === NO_CHALLENGE_KEY && bKey !== NO_CHALLENGE_KEY) return 1;
          if (aKey !== NO_CHALLENGE_KEY && bKey === NO_CHALLENGE_KEY) return -1;

          const aCount = challengeCountMap.get(aKey) ?? 0;
          const bCount = challengeCountMap.get(bKey) ?? 0;

          if (aCount !== bCount) return bCount - aCount;
          // 같은 문의 개수일 때 같은 챌린지끼리 묶이도록 처리
          if (aKey !== bKey) {
            return Number(aKey) - Number(bKey);
          }
          // 같은 챌린지 내에서는 오래된 순
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        });
      }
      case QuestionSortType.LATEST:
      default:
        return _filteredQuestions;
    }
  };

  const setFilter = () => {
    let _filteredQuestions = filterByDropdown();

    _filteredQuestions = _filteredQuestions.filter(
      (question) =>
        filterByselectedChallengeType(question) &&
        filterByAnswer(question) &&
        filterByDivideGroup(question),
    );

    _filteredQuestions = sortQuestion(_filteredQuestions);

    // 태그 옵션은 dropdown 필터에 영향받지 않고
    // 현재 챌린지 타입 내의 모든 챌린지를 기반으로 보여주기 위해 questions 전체를 사용
    const _baseQuestionsForTags = questions.filter((question) =>
      filterByselectedChallengeType(question),
    );
    const countedTags = getFormatTagsObject(_baseQuestionsForTags);

    const noTagCount = _baseQuestionsForTags.filter(
      (q) => !q.tags || q.tags.length === 0,
    ).length;
    const noTagOption = {
      key: NO_TAG_FILTER_VALUE,
      text: `태그없음 (${noTagCount})`,
      value: NO_TAG_FILTER_VALUE,
    };

    setFilteredQuestions(_filteredQuestions);
    setQuestionAllTagOptionsWithNumber([noTagOption, ...countedTags]);
  };

  const getQuestionTags = async () => {
    const _formattedQuestionTagOptions = questionTagList.map((tag: string) => {
      return {
        key: tag,
        text: tag,
        value: tag,
      };
    });
    setQuestionAllTagOptions(_formattedQuestionTagOptions);
  };

  const closeValidationModal = () => setValidationModalOpen(false);

  const handleValidate = async (_validated: boolean) => {
    closeValidationModal();
    await download();
    setValidated(_validated);
  };

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    const res = await apis.question.getQuestionsForDownload({
      gteCreatedAt: startOfDay(new Date(filterStartDatetime)).getTime(),
      ltCreatedAt: startOfDay(
        addDays(new Date(filterEndDatetime), 1),
      ).getTime(),
    });
    const { questions: _questionsForDownload } = res;
    setQuestionsForDownload(_questionsForDownload);
    setDownloading(false);
  };

  const answerQuestionList = async () => {
    setBatchAnswerModalOpen(true);
  };

  const getMergedTagsSingleArray = (_questions: Question[]) => {
    const mergedTags: string[] = [];
    for (let i = 0; i < _questions.length; ++i) {
      _questions[i].tags.forEach((el: string) => mergedTags.push(el));
    }
    return mergedTags;
  };

  const getNumberOfTags = (array: string[]) => {
    return array.reduce((acc: { [x: string]: number }, cur: string) => {
      if (acc[cur]) {
        acc[cur] += 1;
      } else {
        acc[cur] = 1;
      }
      return acc;
    }, {});
  };

  const handleItemClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    { name }: MenuItemProps,
  ) => {
    if (!name) return;
    setSelectedChallengeType(name);
    setSelectedChallengeIds([]);
    initializeCategories();
  };

  const handleFilter = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    { name }: BreadcrumbSectionProps,
  ) => {
    setIsAnswerFilter(name);
  };

  const handleQuestionTagAddition = (
    e: React.SyntheticEvent<HTMLElement, Event>,
    { value }: DropdownProps,
  ) => {
    const formattedQuestionTagOption = {
      key: String(value),
      text: String(value),
      value: String(value),
    };

    setQuestionAllTagOptions([
      ...questionAllTagOptions,
      formattedQuestionTagOption,
    ]);
  };

  const filterQuestion = (questionId: number) => {
    const newQuestions = _.filter(questions, (q) => q.id !== questionId);
    const newFilteredQuestions = _.filter(
      filteredQuestions,
      (q) => q.id !== questionId,
    );
    setQuestions(newQuestions);
    setFilteredQuestions(newFilteredQuestions);
  };

  const handleDivideGroup = (_divideGroup: number) => {
    setDivideGroup(_divideGroup);
  };

  const handleUpdate = async (updated: any) => {
    onUpdateQuestionTemplateOrder({
      values: updated,
    });
  };

  const deleteTemplate = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    { value }: ButtonProps,
  ) => {
    onDeleteQuestionTemplate({ id: value.id });
  };

  const handleCategoryRadioChange = (
    e: React.SyntheticEvent<HTMLElement, Event>,
    { name, value, ...props }: DropdownProps,
  ) => {
    if (name === 'mainCategory' && value) {
      const _subCategories: QuestionSubCategory[] = [];
      questionCategories.forEach((category: QuestionCategory) => {
        if (category.mainCategory === value) {
          _subCategories.push({
            key: category.id,
            value: category.id,
            text: category.mainCategory + ' / ' + category.subCategory,
            mainCategory: category.mainCategory,
          });
        }
      });
      setSubCategories(_subCategories);
      setActiveMainCategory(String(value));
      setSelectedCategory({
        ...selectedCategory,
        mainCategory: String(value),
        subCategoryIds: [],
      });
    } else if (name === 'subCategory') {
      const nextIds = Array.isArray(value) ? (value as number[]) : [];
      if (nextIds.length === 0) {
        initializeCategories();
      }
      setSelectedCategory({
        ...selectedCategory,
        subCategoryIds: nextIds,
      });
    }
  };

  return (
    <div style={{ margin: '40px 80px' }}>
      <Divider hidden />
      <Grid columns="equal">
        <QuestionSideBar
          selectedChallengeType={selectedChallengeType}
          handleItemClick={handleItemClick}
          filterStartDatetime={filterStartDatetime}
          filterEndDatetime={filterEndDatetime}
          setFilterStartDatetime={setFilterStartDatetime}
          setFilterEndDatetime={setFilterEndDatetime}
          questions={questions}
          questionCount={questionCount}
          questionsForDownload={questionsForDownload}
          showAddTemplateModal={() => setAddTemplateModalOpen(true)}
          showOrderTemplateModal={() => setOrderTemplateModalOpen(true)}
          showEditTemplateModal={() => setEditTemplateModalOpen(true)}
          // showValidationModal={() => setValidationModalOpen(true)} // NOTE: 다운로드 요청 사유를 받아야 하는 경우 주석 해제
          onRequestDownloadPress={download}
          validated={validated}
        />
        <Grid.Column width={1} />
        <Grid.Column>
          {isLoading && <Loader active inline="centered" />}
          {!isLoading && (
            <>
              <FeedControl
                selectedChallengeIds={selectedChallengeIds}
                isAnswerFilter={isAnswerFilter}
                handleFilter={handleFilter}
                filteredQuestionsLength={filteredQuestions.length}
                mainCategories={mainCategories}
                activeMainCategory={activeMainCategory}
                handleCategoryRadioChange={handleCategoryRadioChange}
                subCategories={subCategories}
                handleTagRadioChange={(
                  e: React.SyntheticEvent<HTMLElement, Event>,
                  { value }: DropdownProps,
                ) => {
                  if (Array.isArray(value)) {
                    setSelectedTagsForFilter(value as string[]);
                  }
                }}
                questionAllTagOptionsWithNumber={
                  questionAllTagOptionsWithNumber
                }
                challengeOptions={challengeOptions}
                setSelectedChallengeIds={setSelectedChallengeIds}
                handleDivideGroup={handleDivideGroup}
                divideGroup={divideGroup}
                answerQuestionList={answerQuestionList}
                onRefreshQuestionPress={onRefreshQuestionPress}
                selectedSubCategoryIds={selectedCategory.subCategoryIds}
                selectedTagsForFilter={selectedTagsForFilter}
                setSortType={setSortType}
                selectedChallengeType={selectedChallengeType}
              />
              {['CHALLENGE', 'PURCHASE_CHALLENGE', 'RACE', 'COLLABO'].includes(
                selectedChallengeType,
              ) && (
                <Feed>
                  {filteredQuestions.map((question: Question) => (
                    <QuestionItem
                      key={question.id}
                      question={question}
                      templates={questionTemplateList}
                      filterQuestion={filterQuestion}
                      questionAllTagOptions={questionAllTagOptions}
                      handleQuestionTagAddition={handleQuestionTagAddition}
                      questionCategories={questionCategories}
                    />
                  ))}
                </Feed>
              )}
            </>
          )}
        </Grid.Column>
      </Grid>

      <AddTemplateModal
        addTemplateModalOpen={addTemplateModalOpen}
        closeAddTemplateModal={() => setAddTemplateModalOpen(false)}
        onAddQuestionTemplate={onAddQuestionTemplate}
      />

      <OrderTemplateModal
        orderTemplateModalOpen={orderTemplateModalOpen}
        closeOrderTemplateModal={() => setOrderTemplateModalOpen(false)}
        questionTemplateList={questionTemplateList}
        handleUpdate={handleUpdate}
        deleteTemplate={deleteTemplate}
      />

      <EditTemplateModal
        editTemplateModalOpen={editTemplateModalOpen}
        closeEditTemplateModal={() => {
          setEditTemplateModalOpen(false);
        }}
        questionTemplateList={questionTemplateList}
        onEditQuestionTemplate={onEditQuestionTemplate}
      />

      <BatchAnswerModal
        batchAnswerModalOpen={batchAnswerModalOpen}
        onAnswerQuestionList={onAnswerQuestionList}
        closeBatchAnswerModal={() => setBatchAnswerModalOpen(false)}
      />

      <ValidationRequestModal
        // @ts-ignore
        validationModalOpen={validationModalOpen}
        closeValidationModal={closeValidationModal}
        setValidated={handleValidate}
        location={'문의 정보'}
        defaultText={'유저 문의내용 종류 분석'}
      />
      <Dimmer active={downloading}>
        <Loader active inline="centered" />
      </Dimmer>
    </div>
  );
};

export default QuestionContainer;
